import { Popover, PopoverContent, PopoverTrigger } from "@norkron/web-popover";
import type { ReactElement } from "react";
import type { ResolvedCommentVersion } from "../../lib/commentVersionState";

/**
 * The "switch the document to this version?" confirmation on an obsolete
 * comment's quote (owner request 2026-07-28: "when clicking on their quote they
 * should open a popup asking if the user wants to switch the document to the
 * given version, with two small buttons, Cancel and Switch").
 *
 * ── ⛔ WHY `@norkron/web-popover` AND NOT A HAND-ROLLED POPUP ────────────────
 *
 * Esc must dismiss THIS popup and must NOT close the comment modal / composer
 * around it. That is not a detail — it is a bug that was already found and fixed
 * once, for the editor's link popover, and the fix is subtle enough that
 * re-deriving it by hand reliably reproduces the bug:
 *
 *   The surrounding composer binds a CAPTURE-phase Escape handler on an ANCESTOR
 *   (`<div className="composer" onKeyDownCapture={…}>` → `onCancel()`). React
 *   capture handlers fire OUTERMOST-FIRST along the FIBER tree, and portalling
 *   does NOT escape that — React dispatches synthetic events along the fiber path
 *   regardless of which DOM container the node lives in. So a React
 *   `stopPropagation()` inside this popup could never stop the composer closing.
 *
 *   `PopoverContent` composes `@norkron/web-dismiss`'s `useDismiss`, which
 *   registers a NATIVE `keydown` listener on `document` in CAPTURE phase, checks
 *   it is the TOP dismiss layer, and stops the event THERE — before React
 *   dispatches anything at all. That, and only that, satisfies "Esc closes the
 *   popup and not the surrounding modal".
 *
 *   COROLLARY (the trap): do NOT add an "Esc = Cancel" React `onKeyDown` to the
 *   buttons below. The native stopPropagation above means such a handler would
 *   never fire, and its presence would imply a mechanism that is not there.
 *   Dismissal comes from the controlled `onOpenChange(false)`.
 *
 * ── SEMANTICS ───────────────────────────────────────────────────────────────
 *
 * Cancel and Esc are the SAME outcome: dismiss, switch nothing. Only "Switch"
 * navigates, and it delegates to the caller — which routes it into the very same
 * `onViewVersion` the timeline uses, so there is exactly one version-switch code
 * path in the product.
 */
export function VersionSwitchPopover({
  commentId,
  version,
  open,
  onOpenChange,
  onSwitch,
  children,
}: {
  commentId: number;
  /** The version the note cites — where "Switch" goes. */
  version: ResolvedCommentVersion;
  open: boolean;
  /** Controlled open state. `false` arrives from Cancel, Esc, and outside-click
   *  alike — all three mean "dismiss without switching". */
  onOpenChange: (open: boolean) => void;
  onSwitch: () => void;
  /** The trigger — the comment's quote button. */
  children: ReactElement;
}) {
  return (
    <Popover open={open} onOpenChange={onOpenChange} placement="bottom-start">
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        role="dialog"
        aria-label={`Switch the document to ${version.label}?`}
        className="version-switch-pop"
        data-testid={`version-switch-pop-${commentId}`}
      >
        <p
          className="version-switch-pop__text"
          data-testid={`version-switch-text-${commentId}`}
        >
          This note was written against <strong>{version.label}</strong>. Switch
          the document to {version.label}?
        </p>
        <div className="version-switch-pop__actions">
          <button
            type="button"
            className="btn btn--ghost btn--xs"
            data-testid={`version-switch-cancel-${commentId}`}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary btn--xs"
            data-testid={`version-switch-confirm-${commentId}`}
            onClick={() => {
              // Close FIRST, then switch: the switch re-renders the pane (and can
              // unmount this card), and leaving an open popover attached to a
              // node that is about to go away strands its dismiss layer on the
              // stack — which would then swallow the NEXT Escape anywhere in the
              // app.
              onOpenChange(false);
              onSwitch();
            }}
          >
            Switch
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
